import React, { createContext, useContext, useEffect } from 'react';
import { ConfigProvider, theme } from 'antd';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    useEffect(() => {
        // Ensure dark mode class is removed
        document.documentElement.classList.remove('dark');
        
        // Update meta theme-color
        const metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
            metaThemeColor.setAttribute('content', '#6366F1');
        }
    }, []);

    const themeConfig = {
        algorithm: theme.defaultAlgorithm,
        token: {
            colorPrimary: '#C97D60',
            colorInfo: '#6366F1',
            borderRadius: 8,
            controlOutline: '#E8D5CC',
            colorLink: '#C97D60',
            colorSuccess: '#16a34a',
            colorWarning: '#f59e0b',
            colorError: '#ef4444',
            lineHeight: 1.55,
            fontSize: 15,
            colorBgContainer: '#ffffff',
            colorBgElevated: '#ffffff',
            colorBgLayout: '#fafafa',
            colorText: '#1f2937',
            colorTextSecondary: '#6b7280',
            colorBorder: '#e5e7eb',
            colorBorderSecondary: '#f3f4f6',
            colorBgTextHover: '#f3f4f6',
            colorBgTextActive: '#e5e7eb',
        },
        components: {
            Button: {
                borderRadius: 10,
                controlHeight: 36,
                paddingInline: 14,
                fontWeight: 500,
                fontSize: 15,
                defaultHoverBorderColor: '#f97316',
                defaultHoverColor: '#1f2937',
                primaryColor: 'white',
                primaryHoverColor: '#ea580c',
                primaryActiveColor: '#c2410c',
            },
            Input: {
                borderRadius: 12,
                controlHeight: 40,
                colorBgContainer: '#ffffff',
                colorText: '#1f2937',
                colorTextPlaceholder: '#9ca3af',
                colorBorder: '#d1d5db',
                colorBorderHover: '#9ca3af',
                activeShadow: '0 0 0 3px rgba(99,102,241,0.18)',
            },
            Select: {
                borderRadius: 12,
                controlHeight: 40,
                optionSelectedBg: '#fff7ed',
            },
            Card: {
                borderRadiusLG: 16,
                boxShadowTertiary: '0 10px 26px rgba(0,0,0,0.08)',
                colorBgContainer: '#ffffff',
                colorText: '#1f2937',
                colorTextHeading: '#1f2937',
                colorTextDescription: '#6b7280',
            },
            Menu: {
                itemBorderRadius: 8,
                itemHoverColor: '#0f172a',
                itemSelectedBg: '#fff7ed',
                itemSelectedColor: '#0f172a',
            },
            Dropdown: {
                borderRadius: 10,
            },
            Modal: {
                borderRadiusLG: 12,
                colorBgMask: 'rgba(2,6,23,0.45)',
                paddingMD: 20,
            },
            Table: {
                borderRadiusLG: 12,
                headerBg: '#F8FAFC',
                rowHoverBg: '#F1F5F9',
                headerColor: '#0f172a',
                cellPaddingBlockMD: 12,
                cellPaddingInlineMD: 12,
            },
            Tag: {
                borderRadiusSM: 8,
            },
            Checkbox: {
                borderRadiusSM: 6,
            },
            InputNumber: {
                borderRadius: 12,
                controlHeight: 40,
            },
            DatePicker: {
                borderRadius: 12,
                controlHeight: 40,
            },
            TimePicker: {
                borderRadius: 12,
                controlHeight: 40,
            },
        },
    };

    const value = {
        themeConfig,
    };

    return (
        <ThemeContext.Provider value={value}>
            <ConfigProvider theme={themeConfig}>
                {children}
            </ConfigProvider>
        </ThemeContext.Provider>
    );
};

export default ThemeProvider;
